import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  Box,
  List,
  Stack,
  Dialog,
  Button,
  Avatar,
  ListItem,
  Checkbox,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { getUserAvatarUrl } from 'src/services/user.service';

import Iconify from 'src/components/iconify';

export default function ConflictDialog({
  open,
  users,
  onClose,
  onConfirm,
  permissionName,
  isAdding,
}) {
  const { t } = useTranslation();
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const isAnySelected = selectedUserIds.length > 0;

  const toggleUser = (id) =>
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );

  const handleClose = () => {
    setSelectedUserIds([]);
    onClose();
  };

  const isAddingBool = isAdding === true || String(isAdding) === 'true';

  const descriptionText = isAddingBool
    ? t('permissionss.conflict.manuallyRevoked')
    : t('permissionss.conflict.manuallyGranted');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '24px',
          p: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(22, 27, 34, 0.9)'
              : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 800 }}
      >
        <Iconify icon="solar:shield-warning-bold-duotone" width={28} color="#FDA92D" />
        {t('permissionss.conflict.title')}
      </DialogTitle>

      <DialogContent>
        <Typography
          variant="body2"
          sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}
        >
          {descriptionText}
          <Box component="span" sx={{ color: 'text.primary', fontWeight: 900, ml: 0.5 }}>
            {permissionName}
          </Box>
        </Typography>

        {users.length > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              px: 2,
              py: 1,
              mb: 1.5,
              bgcolor: 'rgba(0, 167, 111, 0.04)',
              borderRadius: '12px',
              border: '1px dashed rgba(0, 167, 111, 0.2)',
            }}
          >
            <Checkbox
              size="small"
              checked={selectedUserIds.length === users.length}
              indeterminate={
                selectedUserIds.length > 0 && selectedUserIds.length < users.length
              }
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedUserIds(users.map((u) => u.id));
                } else {
                  setSelectedUserIds([]);
                }
              }}
              sx={{ color: '#00A76F', '&.Mui-checked': { color: '#00A76F' } }}
            />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, color: '#00A76F', fontSize: '0.85rem' }}
            >
              {selectedUserIds.length === users.length
                ? t('buttons.deselectAll') || 'Deselect All'
                : t('buttons.selectAll') || 'Select All'}
            </Typography>
          </Stack>
        )}

        <List
          sx={{
            maxHeight: 280,
            overflow: 'auto',
            bgcolor: 'background.neutral',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {users.map((user) => (
            <ListItem
              key={user.id}
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2} width="100%">
                <Checkbox
                  size="small"
                  checked={selectedUserIds.includes(user.id)}
                  onChange={() => toggleUser(user.id)}
                  sx={{ color: '#00A76F', '&.Mui-checked': { color: '#00A76F' } }}
                />
                <Avatar
                  src={
                    user.avatarVersion
                      ? getUserAvatarUrl(user.id, user.avatarVersion)
                      : null
                  }
                  sx={{
                    width: 45,
                    height: 45,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                  imgProps={{ loading: 'lazy' }}
                >
                  {user.givenName ? user.givenName.charAt(0).toUpperCase() : ''}
                </Avatar>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 800, fontSize: '0.85rem', color: 'text.primary' }}
                  >
                    {user.givenName} {user.familyName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      display: 'block',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                    }}
                  >
                    {user.email}
                  </Typography>
                </Box>
              </Stack>
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 1 }}>
        <Stack spacing={1.5} width="100%">
          <Button
            fullWidth
            onClick={() => onConfirm(selectedUserIds)}
            variant="contained"
            disabled={!isAnySelected}
            startIcon={<Iconify icon="solar:user-check-bold" />}
            sx={{
              bgcolor: '#00A76F',
              fontWeight: 800,
              py: 1.2,
              borderRadius: '12px',
              textTransform: 'none',
              boxShadow: '0 8px 16px rgba(0, 167, 111, 0.24)',
              '&:hover': { bgcolor: '#008F5D', boxShadow: 'none' },
            }}
          >
            {t('permissionss.conflict.syncSelected')}
          </Button>

          <Button
            fullWidth
            onClick={() => onConfirm([])}
            variant="outlined"
            disabled={isAnySelected}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              fontWeight: 800,
              py: 1.2,
              borderRadius: '12px',
              textTransform: 'none',
              '&:hover': { bgcolor: 'action.hover', borderColor: 'text.primary' },
            }}
          >
            {t('permissionss.conflict.skipAndProceed')}
          </Button>
        </Stack>

        <Button
          onClick={handleClose}
          color="inherit"
          sx={{ fontWeight: 800, mt: 1, textTransform: 'none', fontSize: '0.85rem' }}
        >
          {t('buttons.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ConflictDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  users: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  permissionName: PropTypes.string.isRequired,
  isAdding: PropTypes.bool,
};
